/**
 * GMB TypeScript Type Definitions
 * Comprehensive types for Google My Business entities matching database schema
 */

// ==================== Enums ====================

/**
 * GMB Account types
 */
export enum GmbAccountType {
  PERSONAL = 'PERSONAL',
  LOCATION_GROUP = 'LOCATION_GROUP',
  USER_GROUP = 'USER_GROUP',
  ORGANIZATION = 'ORGANIZATION'
}

/**
 * User roles in GMB
 */
export enum GmbRole {
  OWNER = 'OWNER',
  PRIMARY_OWNER = 'PRIMARY_OWNER',
  MANAGER = 'MANAGER',
  SITE_MANAGER = 'SITE_MANAGER'
}

/**
 * Account states
 */
export enum GmbAccountState {
  VERIFIED = 'VERIFIED',
  UNVERIFIED = 'UNVERIFIED',
  SUSPENDED = 'SUSPENDED'
}

/**
 * Location verification status
 */
export enum GmbVerificationStatus {
  VERIFIED = 'VERIFIED',
  UNVERIFIED = 'UNVERIFIED',
  PENDING = 'PENDING',
  SUSPENDED = 'SUSPENDED'
}

/**
 * Post topic types
 */
export type GmbPostTopicType = 'STANDARD' | 'EVENT' | 'OFFER' | 'PRODUCT'

/**
 * Post states
 */
export type GmbPostState = 'LIVE' | 'REMOVED' | 'REJECTED' | 'DRAFT' | 'SCHEDULED'

/**
 * Call to action types
 */
export type GmbCallToActionType = 'LEARN_MORE' | 'RESERVE' | 'ORDER' | 'SHOP' | 'SIGN_UP' | 'CALL' | 'BOOK' | 'GET_OFFER'

/**
 * Sync status types
 */
export enum GmbSyncStatus {
  SUCCESS = 'success',
  PARTIAL = 'partial',
  FAILED = 'failed',
  IN_PROGRESS = 'in_progress'
}

/**
 * Sync types
 */
export enum GmbSyncType {
  ACCOUNTS = 'accounts',
  LOCATIONS = 'locations',
  REVIEWS = 'reviews',
  POSTS = 'posts',
  QUESTIONS = 'questions',
  ALL = 'all'
}

/**
 * Review sources
 */
export enum ReviewSource {
  MANUAL = 'manual',
  GMB = 'gmb',
  JOBBER = 'jobber',
  WEBSITE = 'website',
  OTHER = 'other'
}

/**
 * Star ratings (matching existing reviews table)
 */
export enum StarRating {
  ONE = 'ONE',
  TWO = 'TWO',
  THREE = 'THREE',
  FOUR = 'FOUR',
  FIVE = 'FIVE'
}

// ==================== Interfaces ====================

/**
 * GMB Account entity
 */
export interface GmbAccount {
  id: string
  gmb_account_id: string // e.g., accounts/123456789
  account_name: string
  account_type?: GmbAccountType
  role?: GmbRole
  state?: GmbAccountState
  account_number?: string
  
  // Associations
  franchise_id?: string
  added_by?: string
  
  // OAuth
  refresh_token?: string
  token_expires_at?: Date
  
  // Metadata
  discovered_at?: Date
  last_synced_at?: Date
  is_active: boolean
  created_at: Date
  updated_at: Date
}

/**
 * GMB Location entity
 */
export interface GmbLocation {
  id: string
  gmb_location_id: string // e.g., accounts/123/locations/456
  gmb_account_id: string
  
  // Location details
  location_name: string
  place_id?: string
  primary_phone?: string
  website_url?: string
  
  // Address
  address_street?: string
  address_city?: string
  address_state?: string
  address_postal?: string
  address_country: string
  latitude?: number
  longitude?: number
  
  // Business info
  primary_category?: string
  additional_categories?: string[]
  business_hours?: BusinessHours
  special_hours?: SpecialHours[]
  attributes?: Record<string, any>
  
  // Associations
  franchise_id?: string
  franchise_region?: string
  
  // Status
  verification_status?: GmbVerificationStatus
  is_active: boolean
  is_published: boolean
  
  // Cached metrics
  total_reviews: number
  average_rating: number
  unanswered_questions: number
  
  // Metadata
  discovered_at?: Date
  last_synced_at?: Date
  created_at: Date
  updated_at: Date
}

/**
 * GMB Review entity (specific to GMB)
 */
export interface GmbReview {
  id: string
  gmb_review_id: string
  gmb_location_id: string
  
  // Review details
  reviewer_name: string
  reviewer_photo_url?: string
  is_anonymous: boolean
  
  star_rating: number // 1-5
  comment?: string
  
  // Reply
  reply_comment?: string
  reply_updated_at?: Date
  replied_by?: string
  
  // Timestamps
  review_created_at: Date
  review_updated_at: Date
  
  // Internal tracking
  needs_response: boolean
  is_flagged: boolean
  flag_reason?: string
  
  // Metadata
  synced_at: Date
  created_at: Date
  updated_at: Date
}

/**
 * Extended Review entity (main reviews table)
 */
export interface Review {
  id: string
  reviewer_name: string
  reviewer_email?: string
  reviewer_photo_url?: string
  reviewer_create_time: Date
  reviewer_update_time: Date
  star_rating: StarRating
  reviewer_comment?: string
  reply_comment?: string
  reply_update_time?: Date
  location_name?: string
  
  // GMB specific fields
  gmb_review_id?: string
  gmb_location_id?: string
  source: ReviewSource
  
  // Metadata
  created_at: Date
  updated_at: Date
}

/**
 * GMB Post entity
 */
export interface GmbPost {
  id: string
  gmb_post_id?: string
  gmb_location_id: string
  
  // Post content
  language_code: string
  summary: string
  topic_type: GmbPostTopicType
  
  // Call to action
  cta_type?: GmbCallToActionType
  cta_url?: string
  
  // Media
  media_urls?: MediaItem[]
  
  // Event details (if EVENT type)
  event_title?: string
  event_start_date?: Date
  event_end_date?: Date
  
  // Offer details (if OFFER type)
  offer_coupon_code?: string
  offer_redeem_url?: string
  offer_terms?: string
  
  // Status
  state: GmbPostState
  
  // Performance metrics
  view_count: number
  click_count: number
  
  // Internal
  created_by?: string
  scheduled_for?: Date
  
  // Metadata
  gmb_created_at?: Date
  gmb_updated_at?: Date
  synced_at: Date
  created_at: Date
  updated_at: Date
}

/**
 * GMB Question entity
 */
export interface GmbQuestion {
  id: string
  gmb_question_id: string
  gmb_location_id: string
  
  // Question details
  author_name?: string
  text: string
  
  // Answer
  answer_text?: string
  answered_by?: string
  answered_at?: Date
  
  // Status
  is_answered: boolean
  upvote_count: number
  
  // Metadata
  gmb_created_at: Date
  gmb_updated_at?: Date
  synced_at: Date
  created_at: Date
  updated_at: Date
}

/**
 * GMB Sync Log entity
 */
export interface GmbSyncLog {
  id: string
  sync_type: GmbSyncType
  gmb_account_id?: string
  gmb_location_id?: string
  
  // Results
  status: GmbSyncStatus
  items_synced: number
  items_failed: number
  error_message?: string
  
  // Metadata
  started_at: Date
  completed_at?: Date
  initiated_by?: string
  created_at: Date
}

// ==================== Supporting Types ====================

/**
 * Business hours structure
 */
export interface BusinessHours {
  periods: HoursPeriod[]
}

export interface HoursPeriod {
  openDay: DayOfWeek
  openTime: string // HH:MM format
  closeDay: DayOfWeek
  closeTime: string // HH:MM format
}

export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY'
}

/**
 * Special hours structure
 */
export interface SpecialHours {
  startDate: string // YYYY-MM-DD
  endDate: string // YYYY-MM-DD
  isClosed: boolean
  periods?: HoursPeriod[]
}

/**
 * Media item structure
 */
export interface MediaItem {
  mediaFormat: 'PHOTO' | 'VIDEO'
  sourceUrl: string
  description?: string
}

// ==================== API Response Types ====================

/**
 * GMB API list response wrapper
 */
export interface GmbListResponse<T> {
  [key: string]: T[] // e.g., { accounts: Account[] } or { locations: Location[] }
  nextPageToken?: string
  totalSize?: number
}

/**
 * GMB API error response
 */
export interface GmbErrorResponse {
  error: {
    code: number
    message: string
    status?: string
    details?: Array<{
      '@type': string
      [key: string]: any
    }>
  }
}

// ==================== Form/Input Types ====================

/**
 * Create post input
 */
export interface CreatePostInput {
  gmb_location_id: string
  language_code?: string
  summary: string
  topic_type: GmbPostTopicType
  cta_type?: GmbCallToActionType
  cta_url?: string
  media_urls?: MediaItem[]
  
  // Event specific
  event_title?: string
  event_start_date?: Date
  event_end_date?: Date
  
  // Offer specific
  offer_coupon_code?: string
  offer_redeem_url?: string
  offer_terms?: string
  
  // Scheduling
  scheduled_for?: Date
}

/**
 * Reply to review input
 */
export interface ReplyToReviewInput {
  gmb_location_id: string
  gmb_review_id: string
  reply_comment: string
}

/**
 * Answer question input
 */
export interface AnswerQuestionInput {
  gmb_location_id: string
  gmb_question_id: string
  answer_text: string
}

// ==================== Filter Types ====================

/**
 * Review filters
 */
export interface ReviewFilters {
  gmb_location_id?: string
  star_rating?: number | number[]
  needs_response?: boolean
  is_flagged?: boolean
  date_from?: Date
  date_to?: Date
  search?: string
}

/**
 * Post filters
 */
export interface PostFilters {
  gmb_location_id?: string
  topic_type?: GmbPostTopicType
  state?: GmbPostState
  created_by?: string
  date_from?: Date
  date_to?: Date
}

/**
 * Question filters
 */
export interface QuestionFilters {
  gmb_location_id?: string
  is_answered?: boolean
  date_from?: Date
  date_to?: Date
  search?: string
}

// ==================== Utility Types ====================

/**
 * Pagination parameters
 */
export interface PaginationParams {
  page?: number
  pageSize?: number
  orderBy?: string
  orderDirection?: 'asc' | 'desc'
}

/**
 * Sync options
 */
export interface SyncOptions {
  force?: boolean
  batchSize?: number
  startDate?: Date
  endDate?: Date
  locationIds?: string[]
}

/**
 * Bulk operation result
 */
export interface BulkOperationResult<T> {
  successful: T[]
  failed: Array<{
    item: T
    error: string
  }>
  total: number
  successCount: number
  failureCount: number
}